import React, { type FC } from 'react';
import {
    Row,
    Col,
    Input,
    Select,
    DatePicker,
    InputNumber,
    Tooltip,
    Form,
} from 'antd';
import { CatalogInstallationKeys, CatalogTankKeys } from 'api/generated';

import { volumeFormatter as numberFormatter} from "services/utils/formatterUtil";

const { TextArea } = Input;
const { Option } = Select;

const dateFormat = 'DD/MM/YYYY';


export interface ValidateBoleanExists {
    validateStatus: boolean;
    errorMsg: string;
  }

interface TankFieldsProps {
    containerCodeVS?: ValidateBoleanExists;
    tankAndDuctIdentificationKeyVS?: ValidateBoleanExists;
    tankKeys?: CatalogTankKeys[];
    installationKey?: CatalogInstallationKeys;
    onTankKeyBlur: () => void;
    onCustomerTankKeyBlur: (value: string) => void;
}

const TankFields: FC<TankFieldsProps> = ({
    containerCodeVS,
    tankAndDuctIdentificationKeyVS,
    tankKeys,
    installationKey,
    onTankKeyBlur,
    onCustomerTankKeyBlur,
}) => {
    return (
        <>
            <Row gutter={[16, 0]}>
                <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                    <Tooltip title="Código auditable del tanque, generado automáticamente">
                        <Form.Item
                            name="containerKey"
                            label="Código auditable del tanque (almacén)"
                            validateStatus={containerCodeVS?.validateStatus ? 'error' : 'success'}
                            help={containerCodeVS?.errorMsg}
                            rules={[{ required: true, message: 'El formato de clave de tanque no es válido.' }]}
                        >
                            <Input disabled placeholder="TQS-XXX-XXXX" />
                        </Form.Item>
                    </Tooltip>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                    <Tooltip title="Clave de identificación del medidor, generada automáticamente">
                        <Form.Item
                            name="meterIdentificationKey"
                            label="Clave de identificación del medidor"
                        >
                            <Input disabled placeholder="SME-TQS-XXX-XXXX" />
                        </Form.Item>
                    </Tooltip>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                    <Tooltip title="Incertidumbre de medición del contenedor">
                        <Form.Item
                            name="measurementUncertainty"
                            label="Incertidumbre de medición"
                            rules={[{ required: true, message: 'Introduce la incertidumbre de medición.' }]}
                        >
                            <InputNumber
                                precision={3}
                                formatter={numberFormatter}
                                style={{ width: '100%' }}
                                placeholder="Ej: 0.001"
                            />
                        </Form.Item>
                    </Tooltip>
                </Col>
            </Row>

            <Row gutter={[16, 0]}>
                <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                    <Tooltip title="Tipo de tanque asignado por el almacén">
                        <Form.Item
                            name="tankType"
                            label="Tipo de tanque (almacén)"
                            rules={[{ required: true, message: 'Por favor selecciona el tipo de tanque.' }]}
                        >
                            <Select disabled onBlur={onTankKeyBlur}>
                                {tankKeys?.map((item) => (
                                    <Option key={item.id} value={item.code}>
                                        {item.description}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Tooltip>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                    <Tooltip title="Clave de instalación del tanque">
                        <Form.Item
                            name="instalationKey"
                            label="Clave de instalación (almacén)"
                            initialValue={installationKey?.code}
                            rules={[{ required: true, message: 'Selecciona la clave de instalación.' }]}
                        >
                            <Select disabled>
                                <Option value={installationKey?.code}>
                                    {installationKey?.code} - {installationKey?.description}
                                </Option>
                            </Select>
                        </Form.Item>
                    </Tooltip>
                </Col>
                <Col xs={24} sm={24} md={24} lg={8} xl={8}>
                    <Tooltip title="Código numérico del tanque (4 dígitos)">
                        <Form.Item
                            name="customerTankKey"
                            label="Código del tanque"
                            validateStatus={tankAndDuctIdentificationKeyVS?.validateStatus ? 'error' : 'success'}
                            help={tankAndDuctIdentificationKeyVS?.errorMsg}
                            rules={[{ required: true, message: 'Introduce el código del tanque.' }]}
                        >
                            <Input
                                maxLength={4}
                                placeholder="Ej: 0001"
                                onBlur={(e) => onCustomerTankKeyBlur(e.target.value)}
                            />
                        </Form.Item>
                    </Tooltip>
                </Col>
            </Row>

            <Row gutter={[16, 0]}>
                <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                    <Tooltip title="Localización y descripción del contenedor">
                        <Form.Item
                            name="containerDescription"
                            label="Localización y descripción"
                            rules={[
                                { required: true, message: 'Introduce la descripción.' },
                                { min: 2, message: 'Mínimo 2 caracteres.' },
                                { max: 250, message: 'Máximo 250 caracteres.' },
                            ]}
                        >
                            <TextArea autoSize={{ minRows: 1, maxRows: 6 }} />
                        </Form.Item>
                    </Tooltip>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                    <Tooltip title="Vigencia de calibración del tanque">
                        <Form.Item
                            name="containerCalibrationDate"
                            label="Vigencia de calibración"
                            rules={[{ required: true, message: 'Selecciona la vigencia de calibración.' }]}
                        >
                            <DatePicker style={{ width: '100%' }} format={dateFormat} />
                        </Form.Item>
                    </Tooltip>
                </Col>
                <Col xs={24} sm={24} md={24} lg={8} xl={8}>
                    <Tooltip title="Capacidad total del contenedor">
                        <Form.Item
                            name="containerTotalCapacity"
                            label="Capacidad total"
                            rules={[{ required: true, message: 'Introduce la capacidad total.' }]}
                        >
                            <InputNumber
                                precision={3}
                                formatter={numberFormatter}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Tooltip>
                </Col>
            </Row>

            <Row gutter={[16, 0]}>
                <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                    <Tooltip title="Capacidad operativa del contenedor">
                        <Form.Item
                            name="containerOperationalCapacity"
                            label="Capacidad operativa"
                            rules={[{ required: true, message: 'Introduce la capacidad operativa.' }]}
                        >
                            <InputNumber
                                precision={3}
                                formatter={numberFormatter}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Tooltip>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                    <Tooltip title="Capacidad útil del contenedor">
                        <Form.Item
                            name="containerUsefulCapacity"
                            label="Capacidad útil"
                            rules={[{ required: true, message: 'Introduce la capacidad útil.' }]}
                        >
                            <InputNumber
                                precision={3}
                                formatter={numberFormatter}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Tooltip>
                </Col>
                <Col xs={24} sm={24} md={24} lg={8} xl={8}>
                    <Tooltip title="Capacidad de fondaje del contenedor">
                        <Form.Item
                            name="containerFundingCapacity"
                            label="Capacidad fondaje"
                            rules={[{ required: true, message: 'Introduce la capacidad de fondaje.' }]}
                        >
                            <InputNumber
                                precision={3}
                                formatter={numberFormatter}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Tooltip>
                </Col>
            </Row>

            <Row gutter={[16, 0]}>
                <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                    <Tooltip title="Nivel mínimo operativo de llenado (solo gas natural)">
                        <Form.Item
                            name="containerHeelGasCapacity"
                            label="Nivel mínimo operativo de llenado"
                        >
                            <InputNumber
                                precision={3}
                                formatter={numberFormatter}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Tooltip>
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                    <Tooltip title="Volumen mínimo de operación del contenedor">
                        <Form.Item
                            name="containerMinOperationalCapacity"
                            label="Volumen mínimo de operación"
                            rules={[{ required: true, message: 'Introduce el volumen mínimo de operación.' }]}
                        >
                            <InputNumber
                                precision={3}
                                formatter={numberFormatter}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Tooltip>
                </Col>
                <Col xs={24} sm={24} md={24} lg={8} xl={8}>
                    <Tooltip title="Volumen inicial del contenedor">
                        <Form.Item
                            name="containerInitialVolume"
                            label="Volumen inicial"
                            rules={[{ required: true, message: 'Introduce el volumen inicial.' }]}
                        >
                            <InputNumber
                                precision={3}
                                min={0}
                                formatter={numberFormatter}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Tooltip>
                </Col>
            </Row>
        </>
    );
};

export default TankFields;